/**
 * Organization Row entity class
 */

/**
 * NetworkRow Factory
 * returns Organization Row entity class
 *
 * @class NetworkRowFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.NetworkRow} class
 *
 * @ngdoc factory
 * @name mwApp.factory:NetworkRowFactory
 */
import { has } from 'app/helpers/object'
import { ROW_LOCALIZATION_PAIRS, metadataMapToLocalizations, localizationsToMetadataMap } from 'app/helpers/localization'

/* @ngInject */
export default function NetworkRowFactory ($http, $injector, alertLiteSvc, Entity, FileUploadMixin) {
  /* === NetworkRow class === */

  /**
   * NetworkRow model
   *
   * @class NetworkRow
   * @classdesc NetworkRow model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @mixes models.upDownMixin
   * @return {models.NetworkRow} NetworkRow entity
   */
  function NetworkRow () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  NetworkRow.prototype = Object.create(Entity.prototype)
  NetworkRow.prototype.constructor = NetworkRow

  /* === NetworkRow instance methods & fields === */

  Object.assign(NetworkRow.prototype, {

    /**
     * instance fields
     *
     * @memberof models.NetworkRow#
     * @type {Object}
     */
    _fields: {
      id: Number,
      backgroundStyle: {
        color: String,
        gradientConfig: {
          angle: Number,
          colors: [
            {
              color: String,
              percentage: Number
            }
          ],
          type: 'LINEAR'
        },
        imageUrl: String,
        type: String
      },
      categoryId: Number,
      categoryName: String,
      description: String,
      labels: [String],
      parentCategoryId: Number,
      parentCategoryName: String,
      posterOrientation: String,
      posterSize: String,
      sortOrder: Number,
      shelfReferenceId: null,
      title: String,
      top: null,
      type: String,
      localizationMetadata: {}, // map<langCode, {title, description}>
      // View-only: localizations of the linked content shelf (SHELF rows).
      // Private ($-prefixed) so it is never serialized back to the backend —
      // the row payload has no such field. Populated by fetching the shelf
      // (see addModuleSettings.controller).
      $shelfLocalizations: [{
        language: String,
        name: String,
        description: String
      }]
    },

    /**
     * url template
     *
     * @memberof models.NetworkRow#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/appearance/rows',

    /**
     * public url template
     *
     * @memberof models.NetworkRow#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::networkId}}/rows/{{::id}}',

    /**
     * Prepare row
     *
     * @memberof models.NetworkRow#
     * @override
     */
    _prepare: function () {
      this.$uuid = this.id || Math.floor(Math.random() * 1000000)
      this.localizations = metadataMapToLocalizations(this.localizationMetadata, ROW_LOCALIZATION_PAIRS)
    },

    /**
     * Get underlying object
     *
     * @memberof models.NetworkRow#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      if (!this.labels.includes('SHELF')) {
        delete this.shelfReferenceId
      }
      const isTopWatching = this.type === 'TOP_WATCHING_VODS' || this.type === 'TOP_WATCHING_TV_SHOWS'
      if (!this.labels.includes('SHELF') && !this.labels.includes('EXTERNAL') && !isTopWatching) {
        delete this.top
      }
      if (this.top !== undefined) {
        this.top = !!this.top
      }
      this.localizationMetadata = localizationsToMetadataMap(this.localizations, ROW_LOCALIZATION_PAIRS)
      return Entity.prototype._object.apply(this, arguments)
    }
  }, FileUploadMixin)

  /**
   * Get banner platforms
   *
   * @memberof models.Banner#
   */
  NetworkRow.getAssignedRows = function (networkId) {
    const params = {
      page: 0,
      count: 1000
    }

    // if (Object.prototype.hasOwnProperty.call(tableState, 'q')) {
    //   params.q = tableState.q
    // }

    return $http.get(`/nora/api/networks/${networkId}/appearance/rows?assigned`, { params })
      .data()
      .then((response) => {
        const data = has('payload')(response)
          ? response.payload
          : response

        return this.cast(data, {
          content: [this._name],
          first: true,
          last: true,
          number: 0,
          size: 0
        })
      })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get banner platforms
   *
   * @memberof models.Banner#
   */
  NetworkRow.getAvailableRows = function (networkId, params) {
    return $http.get(`/nora/api/networks/${networkId}/appearance/rows`, { params })
      .data()
      .then((response) => {
        const data = has('payload')(response)
          ? response.payload
          : response

        return this.cast(data, {
          content: [this._name],
          first: true,
          last: true,
          number: 0,
          size: 0
        })
      })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get banner platforms
   *
   * @memberof models.Banner#
   */
  NetworkRow.saveRows = function (rows, networkId) {
    rows.forEach(row => {
      if (row?.backgroundStyle?.type === 'IMAGE' && !row?.backgroundStyle?.imageUrl) {
        row.backgroundStyle.type = 'UNSET'
      }
    })

    return $http.put(`/nora/api/networks/${networkId}/appearance/rows`, rows.map(r => r._object()))
  }

  // copy all static methods from Entity class
  Object.assign(NetworkRow, Entity)
  NetworkRow._name = 'NetworkRow'
  NetworkRow.init()

  // permissions
  NetworkRow.access = {
  }

  // extra
  NetworkRow.extra = {
  }
  return NetworkRow
}
